import {
  ArrowLeft,
  User,
  ChevronDown,
  ChevronUp,
  Search,
  Tag,
  Package,
  ExternalLink,
  TrendingUp,
} from "lucide-react";

export default function VisitorDetail({
  visitorId,
  journey,
  collapsedActions,
  expandedSessions,
  onBack,
  toggleSession,
  toggleActionCollapse,
  getActionKey,
  isCollapsibleAction,
  getActionIcon,
  getActionColor,
  getActionBadgeColor,
  getActionLabel,
  formatTimestamp,
  formatDate,
}) {
  const groupBySession = (journey) => {
    const sessions = {};
    journey.forEach((step) => {
      if (!sessions[step.session_id]) sessions[step.session_id] = [];
      sessions[step.session_id].push(step);
    });
    return sessions;
  };

  const sessions = groupBySession(journey);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* BACK */}
      <button
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6 font-semibold transition"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Visitors List
      </button>

      {/* HEADER */}
      <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Visitor Journey
                </h2>
                <p className="text-sm text-white text-opacity-80 font-mono">
                  {visitorId}
                </p>
              </div>
            </div>

            <div className="bg-white bg-opacity-20 px-4 py-2 rounded-full">
              <span className="text-white font-semibold">
                {Object.keys(sessions).length} Session
                {Object.keys(sessions).length > 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-3 gap-4 p-6 bg-gray-50">
          <Stat label="Total Actions" value={journey.length} />
          <Stat label="Sessions" value={Object.keys(sessions).length} />
          <Stat
            label="Buy Clicks"
            value={
              journey.filter((s) => s.action === "checkout_redirect").length
            }
          />
        </div>
      </div>

      {/* JOURNEY */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {Object.entries(sessions).map(([sessionId, steps], sessionIndex) => (
          <div key={sessionId} className="mb-6 last:mb-0">
            <button
              onClick={() => toggleSession(sessionId)}
              className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-4 rounded-lg transition mb-4"
            >
              <div className="flex items-center">
                <h4 className="text-lg font-semibold text-gray-900 mr-3">
                  Session {sessionIndex + 1}
                </h4>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                  {steps.length} actions
                </span>
                <span className="ml-3 text-sm text-gray-500">
                  {formatDate(steps[0].timestamp)}
                </span>
              </div>
              {expandedSessions[sessionId] ? <ChevronUp /> : <ChevronDown />}
            </button>

            {expandedSessions[sessionId] && (
              <div className="pl-4">
                {steps.map((step, idx) => {
                  const actionKey = getActionKey(sessionId, idx);
                  const isCollapsed = collapsedActions[actionKey] ?? true;
                  const canCollapse = isCollapsibleAction(step.action);

                  return (
                    <div key={idx} className="flex mb-6 last:mb-0">
                      <div className="flex flex-col items-center mr-4">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${getActionColor(
                            step.action,
                          )} border-2`}
                        >
                          {getActionIcon(step.action)}
                        </div>
                        {idx < steps.length - 1 && (
                          <div className="w-0.5 h-16 bg-gray-300 my-2" />
                        )}
                      </div>

                      <div
                        className={`flex-1 p-4 rounded-lg border-2 ${getActionColor(
                          step.action,
                        )}`}
                      >
                        {/* HEADER */}
                        <div className="flex justify-between items-center mb-3">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${getActionBadgeColor(
                              step.action,
                            )}`}
                          >
                            {getActionLabel(step.action)}
                          </span>

                          <div className="flex items-center gap-3">
                            {canCollapse && (
                              <button
                                onClick={() => toggleActionCollapse(actionKey)}
                                className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
                              >
                                {isCollapsed ? "Show" : "Hide"}
                                {isCollapsed ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronUp className="w-4 h-4" />
                                )}
                              </button>
                            )}

                            <span className="text-sm text-gray-600">
                              {formatTimestamp(step.timestamp)}
                            </span>
                          </div>
                        </div>

                        {/* DATA */}
                        <ActionContent step={step} isCollapsed={isCollapsed} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function ActionContent({ step, isCollapsed }) {
  if (step.action === "search") {
    return (
      <>
        <p>Search Query: "{step.data.keyword}"</p>
        {step.data.location && (
          <p>Location: {step.data.location || "Unknown"}</p>
        )}
      </>
    );
  }

  if (step.action === "category_click") {
    return (
      <>
        <p>Category: {step.data.category}</p>
      </>
    );
  }

  if (step.action === "product_click") {
    return (
      <>
        <p className="font-semibold">
          Product:{" "}
          {step.data.product?.title ||
            step.data.product_title ||
            step.data.product?.product_id}
        </p>
      </>
    );
  }

  if (step.action === "checkout_redirect") {
    return (
      <a
        href={step.data.redirect_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline"
      >
        {step.data.redirect_url}
      </a>
    );
  }

  return null;
}
